import { useState, useRef, useCallback, useEffect } from 'react'
import * as Tabs from '@radix-ui/react-tabs'
import { format } from 'date-fns'
import { Image, Plus, Check, X, Monitor, Upload, Loader2, ImagePlus, Expand } from 'lucide-react'
import { usePhotoIndexContext } from '@/features/calendar/PhotoIndexContext'
import { useGoogleAuth } from '@/features/google-photos/GoogleAuthContext'
import { useUpload } from '@/features/upload/UploadContext'
import { useSerialCommands } from '@/features/connection/useSerialCommands'
import { useConnection } from '@/features/connection/ConnectionContext'
import { useToast } from '@/contexts/ToastContext'
import { useImageCache } from '@/hooks/useImageCache'
import { createThumbnail, DISPLAY_WIDTH, DISPLAY_HEIGHT } from '@/features/image-processing/imageProcessor'
import { Button } from '@/components/ui/Button'
import { ImagePreviewModal } from '@/components/ui/ImagePreviewModal'
import { DuplicateWarningModal, DuplicateFile } from '@/components/ui/DuplicateWarningModal'
import { ImageEditorModal } from '@/features/image-editor/ImageEditorModal'
import { cn } from '@/lib/utils'
import { generateFilename } from '@/types/upload'
import { generateId } from '@/lib/utils'

interface PhotosPanelProps {
  selectedDate: Date | null
}

export function PhotosPanel({ selectedDate }: PhotosPanelProps) {
  const [activeTab, setActiveTab] = useState('photos')

  return (
    <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
      <Tabs.List className="flex gap-1 border-b border-zinc-800 pb-2">
        <Tabs.Trigger
          value="photos"
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
            'data-[state=active]:bg-zinc-800 data-[state=active]:text-white',
            'data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white'
          )}
        >
          <Image className="w-4 h-4 inline-block mr-2" />
          Photos
        </Tabs.Trigger>
        <Tabs.Trigger
          value="add"
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
            'data-[state=active]:bg-zinc-800 data-[state=active]:text-white',
            'data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white'
          )}
        >
          <Plus className="w-4 h-4 inline-block mr-2" />
          Add Photos
        </Tabs.Trigger>
      </Tabs.List>

      <div className="flex-1 overflow-auto pt-4">
        <Tabs.Content value="photos" className="h-full">
          <PhotosList selectedDate={selectedDate} />
        </Tabs.Content>
        <Tabs.Content value="add" className="h-full">
          <AddPhotosPanel selectedDate={selectedDate} />
        </Tabs.Content>
      </div>
    </Tabs.Root>
  )
}

interface PhotosListProps {
  selectedDate: Date | null
}

// Track loading state for each photo
interface PhotoLoadingState {
  [filename: string]: 'idle' | 'loading' | 'loaded' | 'error'
}

function PhotosList({ selectedDate }: PhotosListProps) {
  const { getPhotosForDate, getCurrentPhoto, selectPhoto, togglePhotoEnabled } = usePhotoIndexContext()
  const { displayFile, downloadFile } = useSerialCommands()
  const { isConnected } = useConnection()
  const { addToast } = useToast()
  const { getImageUrl, setImage, isReady: cacheReady } = useImageCache()

  const [displayingIndex, setDisplayingIndex] = useState<number | null>(null)
  const [imageUrls, setImageUrls] = useState<{ [filename: string]: string }>({})
  const [loadingState, setLoadingState] = useState<PhotoLoadingState>({})
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null)

  // Load images when date changes
  const dateKey = selectedDate ? format(selectedDate, 'MM-dd') : ''
  const photos = selectedDate ? getPhotosForDate(dateKey) : []

  // Track if we're currently downloading to prevent concurrent operations
  const isDownloadingRef = useRef(false)

  useEffect(() => {
    if (!selectedDate || !cacheReady || photos.length === 0) return

    // Prevent concurrent download operations
    if (isDownloadingRef.current) return

    // Load images for this date
    const loadImages = async () => {
      isDownloadingRef.current = true

      try {
        for (const photo of photos) {
          const filename = `/${photo.file}`

          // Skip if already loaded or loading
          if (imageUrls[filename] || loadingState[filename] === 'loading' || loadingState[filename] === 'loaded') continue

          // Check cache first
          const cachedUrl = await getImageUrl(filename)
          if (cachedUrl) {
            setImageUrls(prev => ({ ...prev, [filename]: cachedUrl }))
            setLoadingState(prev => ({ ...prev, [filename]: 'loaded' }))
            continue
          }

          // Need to download from device
          if (isConnected) {
            setLoadingState(prev => ({ ...prev, [filename]: 'loading' }))

            try {
              const data = await downloadFile(filename)
              await setImage(filename, data)
              const url = await getImageUrl(filename)
              if (url) {
                setImageUrls(prev => ({ ...prev, [filename]: url }))
                setLoadingState(prev => ({ ...prev, [filename]: 'loaded' }))
              }
            } catch (error) {
              console.error(`Failed to download ${filename}:`, error)
              setLoadingState(prev => ({ ...prev, [filename]: 'error' }))
            }
          }
        }
      } finally {
        isDownloadingRef.current = false
      }
    }

    loadImages()
  }, [selectedDate, photos, cacheReady, isConnected, getImageUrl, downloadFile, setImage, imageUrls, loadingState])

  const handleDisplayPhoto = async (dateKey: string, index: number, filename: string) => {
    setDisplayingIndex(index)
    try {
      await displayFile(`/${filename}`)
      await selectPhoto(dateKey, index)
      addToast({
        type: 'success',
        title: 'Photo displayed',
        description: `${filename} is now showing on the display`,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to display photo'
      addToast({
        type: 'error',
        title: 'Display failed',
        description: message,
      })
    } finally {
      setDisplayingIndex(null)
    }
  }

  const handleExpandPhoto = (filename: string) => {
    const url = imageUrls[`/${filename}`]
    if (url) {
      setPreviewImage({ url, filename })
    }
  }

  if (!selectedDate) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
        <Image className="w-12 h-12 mb-4 opacity-50" />
        <p>Select a date on the calendar to view photos</p>
      </div>
    )
  }

  const currentPhoto = getCurrentPhoto(dateKey)

  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
        <Image className="w-12 h-12 mb-4 opacity-50" />
        <p>No photos for {format(selectedDate, 'MMMM d')}</p>
        <p className="text-sm mt-2">Switch to "Add Photos" tab to upload</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white">
        Photos for {format(selectedDate, 'MMMM d')}
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo, index) => {
          const filename = `/${photo.file}`
          const isCurrent = photo.file === currentPhoto
          const isEnabled = photo.enabled
          const isDisplaying = displayingIndex === index
          const isHovered = hoveredIndex === index
          const imageUrl = imageUrls[filename]
          const state = loadingState[filename] || 'idle'

          return (
            <div
              key={photo.file}
              className={cn(
                'relative bg-zinc-900 rounded-lg overflow-hidden border-2 transition-all',
                isCurrent ? 'border-blue-500' : 'border-transparent',
                !isEnabled && 'opacity-50'
              )}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Image container */}
              <div className="aspect-[2/3] bg-zinc-800 flex items-center justify-center relative">
                {state === 'loading' || state === 'idle' ? (
                  <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
                ) : state === 'error' ? (
                  <div className="text-center p-2">
                    <Image className="w-8 h-8 text-zinc-600 mx-auto" />
                    <p className="text-xs text-zinc-500 mt-1">Failed to load</p>
                  </div>
                ) : imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={photo.file}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Image className="w-8 h-8 text-zinc-600" />
                )}

                {/* Expand button on hover */}
                {isHovered && imageUrl && (
                  <button
                    onClick={() => handleExpandPhoto(photo.file)}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-md transition-colors"
                    title="View full size"
                  >
                    <Expand className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>

              {/* Photo info */}
              <div className="p-2 space-y-2">
                <p className="text-xs text-zinc-400 truncate" title={photo.file}>
                  {photo.file}
                </p>

                {/* Actions */}
                <div className="flex gap-1">
                  <Button
                    variant={isCurrent ? 'default' : 'secondary'}
                    size="sm"
                    onClick={() => handleDisplayPhoto(dateKey, index, photo.file)}
                    className="flex-1 text-xs"
                    disabled={isDisplaying}
                  >
                    {isDisplaying ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Displaying...
                      </>
                    ) : isCurrent ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Current
                      </>
                    ) : (
                      <>
                        <Monitor className="w-3 h-3 mr-1" />
                        Display
                      </>
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePhotoEnabled(dateKey, index)}
                    className={cn(
                      'px-2',
                      isEnabled ? 'text-green-500' : 'text-red-500'
                    )}
                    title={isEnabled ? 'Disable photo' : 'Enable photo'}
                  >
                    {isEnabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Current badge */}
              {isCurrent && (
                <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">
                  Current
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Image preview modal */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url ?? null}
        filename={previewImage?.filename}
      />
    </div>
  )
}

interface AddPhotosPanelProps {
  selectedDate: Date | null
}

function AddPhotosPanel({ selectedDate }: AddPhotosPanelProps) {
  const { isSignedIn, isLoading: isAuthLoading, signIn, openPhotoPicker, accessToken, pendingPhotos, consumePendingPhotos } = useGoogleAuth()
  const { isConnected } = useConnection()
  const { addToast } = useToast()
  const { addToQueue } = useUpload()
  const { checkDuplicate } = usePhotoIndexContext()
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [googleStatus, setGoogleStatus] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isProcessingPhotosRef = useRef(false)

  // Duplicate detection state
  const [duplicates, setDuplicates] = useState<DuplicateFile[]>([])
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [pendingItems, setPendingItems] = useState<Array<{
    googleId?: string
    sourceUrl: string
    filename: string
    date: string
    previewUrl: string
    processedData: Uint8Array
  }>>([])

  // Image editor state
  const [showEditorModal, setShowEditorModal] = useState(false)
  const [editorImages, setEditorImages] = useState<Array<{
    id: string
    file?: File
    blob?: Blob
    sourceUrl: string
    filename: string
    date: string
    previewUrl: string
  }>>([])

  // Handle duplicate resolution
  const handleSkipDuplicates = useCallback(() => {
    // Filter out duplicates from pending items
    const duplicateFilenames = new Set(duplicates.map(d => d.filename))
    const itemsToQueue = pendingItems.filter(item => !duplicateFilenames.has(item.filename))

    if (itemsToQueue.length > 0) {
      addToQueue(itemsToQueue)
      addToast({
        type: 'success',
        title: `${itemsToQueue.length} photo${itemsToQueue.length > 1 ? 's' : ''} queued`,
        description: `Skipped ${duplicates.length} duplicate${duplicates.length > 1 ? 's' : ''}`,
      })
    } else {
      addToast({
        type: 'info',
        title: 'All files skipped',
        description: 'No new photos to upload',
      })
    }

    setPendingItems([])
    setDuplicates([])
  }, [duplicates, pendingItems, addToQueue, addToast])

  const handleReplaceDuplicates = useCallback(() => {
    // Queue all items including duplicates (they will be overwritten)
    if (pendingItems.length > 0) {
      addToQueue(pendingItems)
      addToast({
        type: 'success',
        title: `${pendingItems.length} photo${pendingItems.length > 1 ? 's' : ''} queued`,
        description: `${duplicates.length} existing file${duplicates.length > 1 ? 's' : ''} will be replaced`,
      })
    }

    setPendingItems([])
    setDuplicates([])
  }, [pendingItems, duplicates, addToQueue, addToast])

  // Handle editor completion
  const handleEditorComplete = useCallback((processedImages: Array<{
    filename: string
    date: string
    previewUrl: string
    processedData: Uint8Array
  }>) => {
    // Check for duplicates before adding to queue
    const items = processedImages.map(img => ({
      sourceUrl: '',
      filename: img.filename,
      date: img.date,
      previewUrl: img.previewUrl,
      processedData: img.processedData,
    }))

    processAndCheckDuplicates(items)
  }, [])

  // Process items and check for duplicates
  const processAndCheckDuplicates = useCallback((items: Array<{
    googleId?: string
    sourceUrl: string
    filename: string
    date: string
    previewUrl: string
    processedData: Uint8Array
  }>) => {
    const foundDuplicates: DuplicateFile[] = []

    for (const item of items) {
      const result = checkDuplicate(item.filename)
      if (result.exists) {
        foundDuplicates.push({
          filename: item.filename,
          existingDate: result.date || 'unknown',
          newDate: item.date,
        })
      }
    }

    if (foundDuplicates.length > 0) {
      setDuplicates(foundDuplicates)
      setPendingItems(items)
      setShowDuplicateModal(true)
    } else {
      // No duplicates, queue directly
      addToQueue(items)
      addToast({
        type: 'success',
        title: `${items.length} photo${items.length > 1 ? 's' : ''} queued`,
        description: 'Upload will start automatically',
      })
    }
  }, [checkDuplicate, addToQueue, addToast])

  // Process Google Photos (shared between handleChoosePhotos and pending photos effect)
  const processGooglePhotos = useCallback(async (allMedia: Array<{
    id: string
    createTime: string
    mediaFile: { baseUrl: string; mimeType: string }
  }>) => {
    if (isProcessingPhotosRef.current) return
    isProcessingPhotosRef.current = true

    try {
      // Filter out video files
      const photos = allMedia.filter(item => !item.mediaFile.mimeType.startsWith('video/'))
      const skippedVideos = allMedia.length - photos.length

      if (photos.length === 0) {
        if (skippedVideos > 0) {
          addToast({
            type: 'info',
            title: 'No images selected',
            description: `${skippedVideos} video${skippedVideos > 1 ? 's' : ''} skipped (only images are supported)`,
          })
        }
        return
      }

      if (skippedVideos > 0) {
        addToast({
          type: 'info',
          title: `${skippedVideos} video${skippedVideos > 1 ? 's' : ''} skipped`,
          description: 'Only images are supported',
        })
      }

      setGoogleStatus(`Downloading ${photos.length} photos...`)

      // Download photos and prepare for editor
      const BATCH_SIZE = 3
      const editorImagesData: Array<{
        id: string
        blob?: Blob
        sourceUrl: string
        filename: string
        date: string
        previewUrl: string
      }> = []

      let downloadedCount = 0
      let errorCount = 0

      for (let i = 0; i < photos.length; i += BATCH_SIZE) {
        const batch = photos.slice(i, i + BATCH_SIZE)

        const results = await Promise.allSettled(
          batch.map(async (photo) => {
            const createDate = new Date(photo.createTime)
            const month = String(createDate.getMonth() + 1).padStart(2, '0')
            const day = String(createDate.getDate()).padStart(2, '0')
            const year = createDate.getFullYear()

            const photoId = `${month}-${day}_${year}_${photo.id.substring(0, 8)}`
            const filename = `${photoId}.bmp`
            const photoUrl = photo.mediaFile.baseUrl + `=w${DISPLAY_WIDTH * 2}-h${DISPLAY_HEIGHT * 2}`

            const response = await fetch(photoUrl, {
              headers: { 'Authorization': `Bearer ${accessToken}` },
            })

            if (!response.ok) {
              throw new Error(`Failed to download: ${response.status}`)
            }

            const blob = await response.blob()
            const previewUrl = await createThumbnail(blob)

            return {
              id: photo.id,
              blob,
              sourceUrl: photoUrl,
              filename,
              date: `${month}-${day}`,
              previewUrl,
            }
          })
        )

        for (const result of results) {
          if (result.status === 'fulfilled') {
            editorImagesData.push(result.value)
            downloadedCount++
          } else {
            console.error('Failed to download photo:', result.reason)
            errorCount++
          }
        }

        setGoogleStatus(`Downloaded ${downloadedCount}/${photos.length} photos...`)
      }

      setGoogleStatus(null)

      if (editorImagesData.length > 0) {
        setEditorImages(editorImagesData)
        setShowEditorModal(true)

        if (errorCount > 0) {
          addToast({
            type: 'info',
            title: `${errorCount} photo${errorCount > 1 ? 's' : ''} failed`,
            description: 'Some photos could not be downloaded',
          })
        }
      } else {
        addToast({
          type: 'error',
          title: 'Import failed',
          description: 'No photos could be downloaded',
        })
      }
    } finally {
      isProcessingPhotosRef.current = false
      // Clear pending photos after processing
      consumePendingPhotos()
    }
  }, [accessToken, addToast, consumePendingPhotos])

  // Process pending photos when component mounts (handles case where user navigated away during picker)
  useEffect(() => {
    if (pendingPhotos && pendingPhotos.length > 0 && !isProcessingPhotosRef.current) {
      processGooglePhotos(pendingPhotos)
    }
  }, [pendingPhotos, processGooglePhotos])

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || !selectedDate) return

    setIsProcessing(true)
    try {
      const validFiles = Array.from(files).filter(file => {
        if (!file.type.startsWith('image/')) {
          addToast({
            type: 'error',
            title: 'Invalid file',
            description: `${file.name} is not an image`,
          })
          return false
        }
        return true
      })

      if (validFiles.length === 0) {
        setIsProcessing(false)
        return
      }

      // Prepare images for editor (create thumbnails, but don't process yet)
      const editorImagesData = await Promise.all(
        validFiles.map(async (file, index) => {
          try {
            const fileId = generateId().slice(0, 10)
            const filename = generateFilename(selectedDate, `${fileId}_${index}`)
            const previewUrl = await createThumbnail(file)

            return {
              id: `${fileId}_${index}`,
              file,
              sourceUrl: URL.createObjectURL(file),
              filename,
              date: format(selectedDate, 'MM-dd'),
              previewUrl,
            }
          } catch (error) {
            addToast({
              type: 'error',
              title: `Failed to load ${file.name}`,
              description: error instanceof Error ? error.message : 'Unknown error',
            })
            return null
          }
        })
      )

      const validEditorImages = editorImagesData.filter((img): img is NonNullable<typeof img> => img !== null)

      if (validEditorImages.length > 0) {
        // Open editor modal
        setEditorImages(validEditorImages)
        setShowEditorModal(true)
      }
    } finally {
      setIsProcessing(false)
    }
  }, [selectedDate, addToast])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleGoogleSignIn = async () => {
    try {
      await signIn()
    } catch (error) {
      // Error already handled in context
    }
  }

  const handleChoosePhotos = async () => {
    if (!isConnected) {
      addToast({
        type: 'error',
        title: 'Not connected',
        description: 'Please connect to device first',
      })
      return
    }

    try {
      setGoogleStatus('Opening photo picker...')
      const allMedia = await openPhotoPicker()

      if (allMedia.length === 0) {
        setGoogleStatus(null)
        return
      }

      await processGooglePhotos(allMedia)
    } catch (error) {
      setGoogleStatus(null)
      const message = error instanceof Error ? error.message : 'Failed to import photos'
      addToast({
        type: 'error',
        title: 'Import failed',
        description: message,
      })
    }
  }

  if (!selectedDate) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
        <Plus className="w-12 h-12 mb-4 opacity-50" />
        <p>Select a date on the calendar first</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-white">
        Add photos for {format(selectedDate, 'MMMM d, yyyy')}
      </h3>

      {/* Local file upload with drag & drop */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center transition-all',
          isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-700',
          isProcessing && 'pointer-events-none opacity-50'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />

        {isProcessing ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
            <p className="text-white font-medium">Processing images...</p>
            <p className="text-sm text-zinc-500">Converting to e-ink format</p>
          </div>
        ) : (
          <div
            className="cursor-pointer flex flex-col items-center gap-4"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
              isDragging ? 'bg-blue-500' : 'bg-zinc-800'
            )}>
              <Upload className={cn(
                'w-8 h-8',
                isDragging ? 'text-white' : 'text-zinc-400'
              )} />
            </div>
            <div>
              <p className="text-white font-medium">
                {isDragging ? 'Drop images here' : 'Drop images here or click to upload'}
              </p>
              <p className="text-sm text-zinc-500 mt-1">
                Supports JPEG, PNG, WebP - will be converted to 400x600 BMP
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Google Photos integration */}
      <div className="bg-zinc-900 rounded-lg p-4">
        <h4 className="font-medium text-white mb-2">Google Photos</h4>

        {isAuthLoading ? (
          <div className="flex items-center gap-2 text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Checking session...</span>
          </div>
        ) : isSignedIn ? (
          <div className="space-y-3">
            <p className="text-sm text-green-500">Connected to Google Photos</p>
            {googleStatus ? (
              <div className="flex items-center gap-2 text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{googleStatus}</span>
              </div>
            ) : (
              <Button
                variant="default"
                onClick={handleChoosePhotos}
                disabled={!isConnected}
              >
                <ImagePlus className="w-4 h-4 mr-2" />
                Choose Photos
              </Button>
            )}
            {!isConnected && (
              <p className="text-xs text-zinc-500">Connect to device to upload photos</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-zinc-500">
              Connect your Google Photos account to easily import photos
            </p>
            <button
              onClick={handleGoogleSignIn}
              className="flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg border border-gray-300 shadow-sm transition-all hover:shadow"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </button>
          </div>
        )}
      </div>

      {/* Info about processing */}
      <div className="bg-zinc-900/50 rounded-lg p-4 text-sm text-zinc-400">
        <h4 className="font-medium text-white mb-2">Image Processing</h4>
        <ul className="list-disc list-inside space-y-1">
          <li>Images are resized to 400x600 pixels (e-ink display size)</li>
          <li>Colors are converted to 6-color palette (Black, White, Yellow, Red, Blue, Green)</li>
          <li>Floyd-Steinberg dithering is applied for better gradients</li>
          <li>Output format is 4-bit indexed BMP (~117KB)</li>
        </ul>
      </div>

      {/* Duplicate Warning Modal */}
      <DuplicateWarningModal
        open={showDuplicateModal}
        onOpenChange={setShowDuplicateModal}
        duplicates={duplicates}
        onSkipAll={handleSkipDuplicates}
        onReplaceAll={handleReplaceDuplicates}
        onSkipSelected={() => {}}
      />

      {/* Image Editor Modal */}
      <ImageEditorModal
        open={showEditorModal}
        onOpenChange={setShowEditorModal}
        initialImages={editorImages}
        onComplete={handleEditorComplete}
      />
    </div>
  )
}
